import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useLocation, useSearch } from 'wouter';

import { useConversationContext } from '@/contexts/v2/ConversationContext';
import { useProfileSettings } from '@/hooks/v2/useProfileSettings';
import { useShowHomeChat } from '@/hooks/v2/useShowHomeChat';
import { useIsMobile } from '@/hooks/use-mobile';
import type { ChatLayoutMode } from '@shared/v2-schemas';
import { cn } from '@/lib/utils';
import {
  getRouteBucket,
  isV2ConversationSurface,
  resolveChatLayoutMode,
  resolveChatNavIntent,
} from '@/lib/chatLayout';

import { ChatThread } from './ChatThread';
import { ConversationPeekBar } from './ConversationPeekBar';
import { ConversationStatusLine } from './ConversationStatusLine';
import { LastResponseCard } from './LastResponseCard';
import { useExpandShortcut } from './useExpandShortcut';
import { WiredConversationErrorNotice } from './WiredConversationErrorNotice';
import { WiredConversationInputBar } from './WiredConversationInputBar';

/** Soft bottom gradient that lifts the chat off the page in non-focused modes
    (handoff ProjectFrame fade scrim): 200px in peek, 360px in detailed, masked
    so it fades in from the top. Behind the chat (z-30), non-interactive. */
function ChatFadeScrim({ mode }: { mode: 'peek' | 'detailed' }) {
  return createPortal(
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 bottom-0 z-30"
      style={{
        height: mode === 'peek' ? 200 : 360,
        background:
          'linear-gradient(to bottom, transparent 0%,' +
          ' color-mix(in srgb, var(--chat-fade-end) 35%, transparent) 35%,' +
          ' var(--chat-fade-mid) 65%, var(--chat-fade-end) 100%)',
        backdropFilter: 'blur(2px)',
        WebkitMaskImage:
          'linear-gradient(to bottom, transparent 0%, black 40%, black 100%)',
        maskImage:
          'linear-gradient(to bottom, transparent 0%, black 40%, black 100%)',
        transition: 'height 220ms ease',
      }}
    />,
    document.body,
  );
}

export function ConversationShell() {
  const [pathname, setLocation] = useLocation();
  const search = useSearch();
  const { data: settings, setChatLayoutPref, setMode } = useProfileSettings();
  const conversation = useConversationContext();
  const showHomeChat = useShowHomeChat();
  const conversationHasUserMessages = conversation.turns.some((t) => t.role === 'user');
  const peekBarRef = useRef<HTMLDivElement | null>(null);
  const prevFocusedRef = useRef(false);

  /** Home (`/`) is owned entirely by `<HomeLobby>` now — the shell renders no
      conversation surface there. The home conversation lives at `/chat`
      (sending from the lobby navigates there). */
  const isHome = pathname === '/' || pathname === '';

  // Voice never enters the page's chat-only 'focused' mode — the dashboard
  // stays visible. The voice bar's expanded card is a separate, bar-local
  // concern (voiceBarExpanded below), decoupled from page focused mode.
  const voiceInput = conversation.inputMode === 'voice';
  // Defaults to the compact bar on mobile, the expanded card on tablet/desktop
  // — `null` means "no manual override yet, follow the viewport"; once the
  // user taps the expand/collapse toggle, their choice sticks regardless of
  // viewport until they toggle again.
  const isMobile = useIsMobile();
  const [voiceBarExpandedOverride, setVoiceBarExpandedOverride] = useState<boolean | null>(null);
  const voiceBarExpanded = voiceBarExpandedOverride ?? !isMobile;
  const prefersReducedMotion = useReducedMotion();

  const bucket = getRouteBucket(pathname);
  const savedPref = settings?.chatLayoutPrefs?.[bucket];
  // `/chat` is the dedicated full-page home conversation — hard-forced to
  // focused (no dashboard behind it), ignoring any saved pref. Other surfaces
  // resolve normally; resolveChatLayoutMode drops legacy `'focus'` values and
  // falls through to the route default.
  const isChatRoute = pathname === '/chat';
  // `?from=chat` opens this surface focused for THIS view only — a transient
  // override that is never written to the saved bucket pref, so a later *direct*
  // visit to a project page defaults to `detailed` (dashboard visible). ANA-193.
  const [navFocusedPath, setNavFocusedPath] = useState<string | null>(null);
  const navFocused = !isChatRoute && !voiceInput && navFocusedPath === pathname;
  const resolvedMode: ChatLayoutMode = isChatRoute
    ? 'focused'
    : navFocused
      ? 'focused'
      : resolveChatLayoutMode(pathname, conversationHasUserMessages, savedPref);
  // In voice, coerce 'focused' → 'detailed' so a *dashboard* page never goes
  // chat-only. `/chat` has no dashboard behind it, so voice stays focused there.
  const mode: ChatLayoutMode =
    !isChatRoute && voiceInput && resolvedMode === 'focused' ? 'detailed' : resolvedMode;
  const focused = mode === 'focused';
  const peek = mode === 'peek';
  const detailed = mode === 'detailed';

  // Honour `?from=chat`: when the chat composer initiated the navigation, focus
  // this surface for the current view only (a transient override, NOT persisted
  // to the bucket pref), then strip the param so a refresh doesn't re-trigger.
  // A pathname change *without* the intent drops any stale transient focus, so a
  // later direct visit to a previously chat-navigated page defaults to detailed.
  // Voice never goes focused. ANA-193.
  const lastNavPathRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const intent = resolveChatNavIntent(search);
    if (intent && !voiceInput && !isChatRoute) {
      setNavFocusedPath(pathname);
    } else if (lastNavPathRef.current !== pathname) {
      setNavFocusedPath(null);
    }
    lastNavPathRef.current = pathname;
    if (intent) {
      const params = new URLSearchParams(search);
      params.delete('from');
      const next = params.toString();
      setLocation(next ? `${pathname}?${next}` : pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pathname, voiceInput, isChatRoute]);

  // Peek-bar status/accent is derived inside ConversationPeekBar via
  // derivePeekStatus (FR-007) from the live conversation + voice state, so the
  // shell no longer passes an override. The four canonical variants and their
  // accents live in peekStatus.ts.

  /** Update the saved preference. The hook reverts on PATCH failure. An explicit
      choice supersedes any transient `?from=chat` focus, so clear it. */
  const setModePref = (next: ChatLayoutMode) => {
    if (navFocusedPath !== null) setNavFocusedPath(null);
    void setChatLayoutPref(bucket, next).catch(() => {
      // PATCH failed; cache reverted by hook.
    });
  };

  /** In-bar Maximize2/Minimize2 toggle — flips Detailed ↔ Focused. */
  const handleToggleFocused = () => {
    setModePref(focused ? 'detailed' : 'focused');
  };

  /** Peek pill tap → open the chat into detailed (the design's "open chat" flow). */
  const handleExpandFromPeek = () => {
    setModePref('detailed');
  };

  /** LastResponseCard X button → collapse back to peek. */
  const handleCollapseToPeek = () => {
    setModePref('peek');
  };

  /** ⌘K / Ctrl+K expands the peek pill to detailed (FR-002, SC-006). No-op
      when already expanded or when focus is inside an editable field. */
  useExpandShortcut(peek, handleExpandFromPeek);

  useEffect(() => {
    if (!isV2ConversationSurface(pathname)) return;
    if (isHome) return;
    if (!focused) return;
    document.body.classList.add('chat-focused');
    return () => document.body.classList.remove('chat-focused');
  }, [focused, isHome, pathname]);

  /** Restore keyboard focus to the peek bar when leaving focused mode. */
  useEffect(() => {
    if (prevFocusedRef.current && !focused && peekBarRef.current) {
      peekBarRef.current.focus();
    }
    prevFocusedRef.current = focused;
  }, [focused]);

  if (!isV2ConversationSurface(pathname)) {
    return null;
  }

  // Home (`/`) is owned by <HomeLobby>; the shell renders no conversation
  // surface there (the home conversation lives at `/chat`). Also avoids a
  // duplicate input bar with the lobby.
  if (isHome) {
    return null;
  }

  // Home Chat gate (ANA-263): the shell — not ChatPageV2 — is what portals the
  // `/chat` thread + composer, so the gate must live here too. For a gated user
  // (non-admin while FEATURE_HOME_CHAT is off) render no home conversation
  // surface at all; ChatPageV2 additionally redirects them home. Project-page
  // conversations (other surfaces) are unaffected.
  if (isChatRoute && !showHomeChat) {
    return null;
  }

  /* ── Peek mode ─────────────────────────────────────────────────────
     The collapsed resting state stacks the slim peek pill (handoff PeekHint)
     ABOVE the input bar. The composer is never hidden — clicking the pill
     (or ⌘K) expands the collapsed last response back to detailed, while the
     input bar stays put so the user can keep typing without expanding. */
  if (peek) {
    return createPortal(
      <>
        <ChatFadeScrim mode="peek" />
        <div
          role="region"
          aria-label="Conversation"
          data-testid="conversation-shell"
          data-shell-mode={mode}
          className="fixed inset-x-0 bottom-0 z-40 mx-auto flex max-w-[820px] flex-col gap-2 px-3 pb-3"
          // Clear the notch / home indicator on safe-area devices (falls back
          // to the pb-3 class where env() is unsupported).
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom) + var(--keyboard-inset, 0px))' }}
        >
          <ConversationPeekBar
            ref={peekBarRef}
            onOpen={handleExpandFromPeek}
            onOpenAndStartVoice={() => {
              handleExpandFromPeek();
              conversation.setInputMode('voice');
              void conversation.connectVoice();
              void setMode('voice');
            }}
          />
          {/* Text-turn failure (n8n error / timeout / empty) → inline error +
              Retry above the composer (ANA-194). Self-gates to text mode. */}
          <WiredConversationErrorNotice />
          {/* Chat input bar must never be hidden. The in-bar Detailed↔Focused
              toggle is auto-suppressed for peek by ModeToggleButton, so the
              bar shows just the composer here. */}
          <WiredConversationInputBar
            mode={mode}
            onToggleMode={voiceInput ? undefined : handleToggleFocused}
            expanded={voiceInput ? voiceBarExpanded : undefined}
            onToggleExpand={voiceInput ? () => setVoiceBarExpandedOverride(!voiceBarExpanded) : undefined}
          />
        </div>
      </>,
      document.body,
    );
  }

  /* ── Detailed (desktop + mobile) ─────────────────────────────────
     LastResponseCard above the input bar — no transcript, no widgets.
     The card returns `null` when there's no last assistant turn yet,
     so detailed-with-empty-thread degrades cleanly to "just the input
     bar pinned at the bottom". */
  if (detailed) {
    return createPortal(
      <>
        <ChatFadeScrim mode="detailed" />
        <div
          role="region"
          aria-label="Conversation"
          data-testid="conversation-shell"
          data-shell-mode={mode}
          // No wrapper card: the LastResponseCard and the input bar each carry
          // their own handoff glass chrome, rendered as two separate stacked
          // cards with a gap (handoff GlobalChat). A wrapping `chat-shell-card`
          // here painted a second box behind them.
          className="fixed inset-x-0 bottom-0 z-40 mx-auto flex max-w-[820px] flex-col gap-2 px-3 pb-3"
          // Clear the notch / home indicator on safe-area devices (falls back
          // to the pb-3 class where env() is unsupported).
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom) + var(--keyboard-inset, 0px))' }}
        >
          {/* Text-mode processing cue ("Ready → Thinking… → Responding →
              Anaka replied"). Detailed mode otherwise shows no in-flight
              feedback — the card ignores it and is null until the first reply.
              Voice has its own status chrome in the bar, so text-only here. */}
          {!voiceInput ? <ConversationStatusLine /> : null}
          {/* Text only: the last *text* response card has no place behind the
              voice chrome (which is fixed/pinned at z-50 and would render the
              card peeking out behind it). Voice has its own status chrome in the
              bar, so suppress the card in voice exactly like the status line. */}
          {!voiceInput ? <LastResponseCard onClose={handleCollapseToPeek} /> : null}
          {/* Text-turn failure (n8n error / timeout / empty) → inline error +
              Retry above the composer (ANA-194). Self-gates to text mode. */}
          <WiredConversationErrorNotice />
          {/* Text: the detailed↔focused control is the in-bar Maximize2 button
              (ModeToggleButton). Voice: no page-focused mode — instead the bar
              carries its own small↔expanded toggle (voiceBarExpanded).
              NOTE: the framer-motion `layout` morph (spec 015 T032) was reverted
              after QA — inside this fixed/flex portal it created a stacking
              context (willChange) + FLIP repositioning that rendered the voice
              card behind/over the LastResponseCard. A proper morph needs
              browser iteration; re-opened in PL-260605 (US1 punch-list). */}
          <WiredConversationInputBar
            mode={mode}
            onToggleMode={voiceInput ? undefined : handleToggleFocused}
            expanded={voiceInput ? voiceBarExpanded : undefined}
            onToggleExpand={voiceInput ? () => setVoiceBarExpandedOverride(!voiceBarExpanded) : undefined}
          />
        </div>
      </>,
      document.body,
    );
  }

  /* ── Focused (desktop + mobile) ────────────────────────────────────
     Full transcript via ChatThread rendered inline on the page (no modal
     Sheet on mobile any more) — the panel is offset below the global nav
     via `--anaka-nav-height`, so the nav stays visible and is the escape
     route. The thread surface goes transparent so the page background
     shows through; widgets are hidden via the `chat-focused` body class
     (see `index.css`). Exit to detailed is the in-bar minimize toggle. */
  return createPortal(
    <motion.div
      role="region"
      aria-label="Conversation"
      data-testid="conversation-shell"
      data-shell-mode={mode}
      className={cn(
        'fixed z-20 mx-auto flex flex-col',
        'top-[var(--anaka-nav-height,7rem)] inset-x-0 bottom-0 max-w-none bg-transparent',
      )}
      // Gentle fade-in as the focused thread arrives (e.g. landing on `/chat`
      // after the lobby fades out). Opacity-only — no `layout`/FLIP here, which
      // previously mis-layered the detailed voice card (see note above).
      initial={prefersReducedMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: prefersReducedMotion ? 0 : 0.24, ease: 'easeOut' }}
    >
      <ChatThread
        shellMode={mode}
        onToggleShellMode={isChatRoute ? undefined : handleToggleFocused}
        surface="transparent"
      />
    </motion.div>,
    document.body,
  );
}
